<template>
  <div class="space-y-6">
    <div class="bg-white rounded-lg shadow p-6">
      <div class="flex justify-between items-center mb-6">
        <h2 class="text-xl font-bold">Staff</h2>
        <button @click="openForm" class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
          ➕ New Staff
        </button>
      </div>

      <div v-if="masterDataStore.loading" class="text-center py-12">
        <div class="inline-block spinner w-8 h-8"></div>
      </div>
      <div v-else-if="masterDataStore.staff.length === 0" class="text-center py-12 text-gray-500">
        No staff found
      </div>
      <div v-else class="overflow-x-auto">
        <table class="w-full">
          <thead class="bg-gray-100 border-b">
            <tr>
              <th class="px-6 py-3 text-left text-sm font-semibold">Name</th>
              <th class="px-6 py-3 text-left text-sm font-semibold">Email</th>
              <th class="px-6 py-3 text-left text-sm font-semibold">District</th>
              <th class="px-6 py-3 text-left text-sm font-semibold">Status</th>
              <th class="px-6 py-3 text-left text-sm font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody class="divide-y">
            <tr v-for="member in masterDataStore.staff" :key="member.id" class="hover:bg-gray-50">
              <td class="px-6 py-4 text-sm font-medium">{{ member.full_name || member.name }}</td>
              <td class="px-6 py-4 text-sm text-gray-600">{{ member.email }}</td>
              <td class="px-6 py-4 text-sm">{{ getDistrictName(member.district_id) }}</td>
              <td class="px-6 py-4 text-sm">
                <span :class="[
                  'px-2 py-1 rounded text-xs',
                  String(member.is_active) === '1' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                ]">
                  {{ String(member.is_active) === '1' ? 'Active' : 'Inactive' }}
                </span>
              </td>
              <td class="px-6 py-4 text-sm space-x-2">
                <button @click="editStaff(member)" class="text-blue-600 hover:text-blue-800">Edit</button>
                <button @click="deleteStaffFunc(member.id)" class="text-red-600 hover:text-red-800">Delete</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Form Modal -->
    <div v-show="showForm" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div class="bg-white rounded-lg shadow-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto z-50 relative">
        <h3 class="text-lg font-bold mb-4">{{ editingStaff ? 'Edit Staff' : 'New Staff' }}</h3>
        <form @submit.prevent="submitForm" class="space-y-4">
          <!-- Login Credentials Section -->
          <div class="pb-3 border-b">
            <h4 class="text-sm font-semibold text-gray-700 mb-3">Login Credentials</h4>
            <div class="space-y-3">
              <div>
                <label class="block text-sm font-medium mb-1">Email (Username) <span class="text-red-500">*</span></label>
                <input 
                  v-model="formData.email" 
                  type="email" 
                  placeholder="staff@example.com" 
                  class="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-600" 
                  :required="!editingStaff" 
                  :disabled="editingStaff !== null"
                />
                <p v-if="editingStaff" class="text-xs text-gray-500 mt-1">Email cannot be changed</p>
              </div>
              <div v-if="!editingStaff">
                <label class="block text-sm font-medium mb-1">Password <span class="text-red-500">*</span></label>
                <input 
                  v-model="formData.password" 
                  type="password" 
                  placeholder="Minimum 8 characters" 
                  class="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-600" 
                  required 
                  minlength="8"
                />
              </div>
              <div v-if="!editingStaff">
                <label class="block text-sm font-medium mb-1">Confirm Password <span class="text-red-500">*</span></label>
                <input 
                  v-model="formData.confirmPassword" 
                  type="password" 
                  placeholder="Re-enter password" 
                  class="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-600" 
                  required 
                  minlength="8"
                />
              </div>
              <div v-if="editingStaff" class="bg-blue-50 p-3 rounded">
                <p class="text-sm text-blue-800">💡 To reset password, use the "Reset Password" option in actions menu</p>
              </div>
            </div>
          </div>

          <!-- Staff Information Section -->
          <div>
            <h4 class="text-sm font-semibold text-gray-700 mb-3">Staff Information</h4>
            <div class="space-y-3">
              <div>
                <label class="block text-sm font-medium mb-1">Full Name <span class="text-red-500">*</span></label>
                <input v-model="formData.full_name" placeholder="John Smith" class="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-600" required />
              </div>
              <div>
                <label class="block text-sm font-medium mb-1">District <span class="text-red-500">*</span></label>
                <select v-model="formData.district_id" class="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-600" required>
                  <option value="">Select District</option>
                  <option v-for="district in masterDataStore.districts" :key="district.id" :value="district.id">
                    {{ district.name }}
                  </option>
                </select>
              </div>
            </div>
          </div>

          <div class="flex items-center">
            <input v-model="formData.is_active" type="checkbox" id="staff-active" class="w-4 h-4" />
            <label for="staff-active" class="ml-2 text-sm">Active</label>
          </div>
          <div class="flex justify-end gap-2 pt-4 border-t">
            <button type="button" @click="closeForm" class="px-4 py-2 border rounded hover:bg-gray-50">Cancel</button>
            <button type="submit" class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">{{ editingStaff ? 'Update' : 'Create' }}</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import Swal from 'sweetalert2'
import { useMasterDataStore } from '@/stores/masterDataStore'

const masterDataStore = useMasterDataStore()
const showForm = ref(false)
const editingStaff = ref(null)
const formData = ref({ 
  email: '', 
  password: '', 
  confirmPassword: '',
  full_name: '', 
  district_id: '', 
  is_active: true 
})

const getDistrictName = (districtId) => {
  const d = masterDataStore.districts.find(d => String(d.id) === String(districtId))
  return d ? d.name : 'N/A'
}

const submitForm = async () => {
  try {
    // Validate required fields for new staff
    if (!editingStaff.value) {
      if (!formData.value.email || !formData.value.password || !formData.value.full_name || !formData.value.district_id) {
        Swal.fire({
          icon: 'warning',
          title: 'Missing Fields',
          text: 'Email, Password, Full Name, and District are required',
          confirmButtonColor: '#3085d6'
        })
        return
      }

      // Validate password match
      if (formData.value.password !== formData.value.confirmPassword) {
        Swal.fire({
          icon: 'warning',
          title: 'Password Mismatch',
          text: 'Password and Confirm Password do not match',
          confirmButtonColor: '#3085d6'
        })
        return
      }

      // Validate password length
      if (formData.value.password.length < 8) {
        Swal.fire({
          icon: 'warning',
          title: 'Weak Password',
          text: 'Password must be at least 8 characters',
          confirmButtonColor: '#3085d6'
        })
        return
      }
    }

    const data = {
      full_name: formData.value.full_name,
      district_id: Number(formData.value.district_id),
      is_active: formData.value.is_active ? 1 : 0
    }

    // Add email and password only for new staff
    if (!editingStaff.value) {
      data.email = formData.value.email
      data.password = formData.value.password
    }

    if (editingStaff.value) {
      await masterDataStore.updateStaff(editingStaff.value.id, data)
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Staff updated successfully',
        confirmButtonColor: '#3085d6'
      })
    } else {
      await masterDataStore.createStaff(data)
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Staff created successfully. Login credentials sent.',
        confirmButtonColor: '#3085d6'
      })
    }
    closeForm()
    await masterDataStore.getStaff()
  } catch (error) {
    console.error('Form submission error:', error)
    const serverErrors = error.response?.data?.errors
    const errorText = typeof serverErrors === 'string'
      ? serverErrors
      : serverErrors && typeof serverErrors === 'object'
        ? Object.values(serverErrors).filter(Boolean).join(', ')
        : error.response?.data?.message || error.message || 'Failed to save staff'

    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: errorText,
      confirmButtonColor: '#3085d6'
    })
  }
}

const closeForm = () => {
  showForm.value = false
  editingStaff.value = null
  formData.value = { 
    email: '', 
    password: '', 
    confirmPassword: '',
    full_name: '', 
    district_id: '', 
    is_active: true 
  }
}

const openForm = () => {
  console.log('Opening staff form...')
  showForm.value = true
}

const editStaff = (member) => {
  editingStaff.value = member
  formData.value = {
    ...member,
    full_name: member.full_name || member.name || '',
    is_active: String(member.is_active) === '1' || member.is_active === true
  }
  showForm.value = true
}

const deleteStaffFunc = async (id) => {
  Swal.fire({
    title: 'Confirm Delete',
    text: 'Are you sure you want to delete this staff member?',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#d33',
    cancelButtonColor: '#3085d6',
    confirmButtonText: 'Yes, delete it',
    cancelButtonText: 'Cancel'
  }).then(async (result) => {
    if (result.isConfirmed) {
      await masterDataStore.deleteStaff(id)
      Swal.fire({
        icon: 'success',
        title: 'Deleted',
        text: 'Staff member deleted successfully',
        confirmButtonColor: '#3085d6'
      })
    }
  })
}

onMounted(async () => {
  await masterDataStore.getStaff()
  await masterDataStore.getDistricts()
})
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
